import { contextValues } from '@streem/logger';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, take } from 'rxjs/operators';
import { streem } from 'streem-sdk-protobuf';
import { logger } from './logger';
export function onRoomStatus({ status, source, }) {
    return source.pipe(filter((s) => status === s), take(1));
}
export function createRoomStatusObservable(roomStatusSource, localParticipantStatusSource, remoteParticipantStatusSource, invitationStatusSource) {
    return combineLatest([
        roomStatusSource,
        localParticipantStatusSource,
        remoteParticipantStatusSource,
        invitationStatusSource,
    ]).pipe(map(([roomStatus, localParticipantStatus, remoteParticipantStatus, invitationStatus]) => buildRoomSessionStatus({
        roomStatus,
        invitationStatus,
        localParticipantStatus,
        remoteParticipantStatus,
    })), distinctUntilChanged((x, y) => x === y));
}
// note that there is overlap between the various conditions, but
// it was felt that it improves readability to spell each condition out
// explicitly and not use one condition inside another, for example
const buildRoomSessionStatus = (config) => {
    logger.setContextValue(contextValues.SESSION_STATUS_CONFIG, config);
    if (checkDisconnectedConditions(config)) {
        return 'DISCONNECTED';
    }
    if (checkConnectedConditions(config)) {
        return 'CONNECTED';
    }
    if (checkConnectingConditions(config)) {
        return 'CONNECTING';
    }
    if (checkDisconnectingConditions(config)) {
        return 'DISCONNECTING';
    }
    logger.warn('Unable to determine room session status, received status', config);
    return 'UNKNOWN';
};
const checkDisconnectedConditions = (config) => {
    const { roomStatus, remoteParticipantStatus, invitationStatus } = config;
    const retVal = (roomStatus === streem.api.Room.Status.STATUS_CLOSED ||
        invitationStatus === streem.api.RoomInvitation.Status.STATUS_REJECTED ||
        invitationStatus === streem.api.RoomInvitation.Status.STATUS_CANCELED ||
        invitationStatus === streem.api.RoomInvitation.Status.STATUS_TIMED_OUT ||
        /**
         * If the back end finds that a participant has not sent heartbeats for two minutes, it will update the room
         * participant's status to LEFT, but it will not update the room status. So, we have to watch the remote
         * participant's status here and end the call if they've left. Otherwise, we'll just sit here like a fool
         * talking to nobody forever until the end of time.
         */
        remoteParticipantStatus === streem.api.RoomParticipant.Status.STATUS_LEFT);
    if (retVal) {
        logger.debug(`Room DISCONNECTED Conditions Met: roomStatus=${roomStatus} remoteParticipantStatus=${remoteParticipantStatus} invitationStatus=${invitationStatus}`);
    }
    return retVal;
};
const checkConnectingConditions = (config) => {
    const { roomStatus, invitationStatus, localParticipantStatus, remoteParticipantStatus, } = config;
    const retVal = (roomStatus !== streem.api.Room.Status.STATUS_CLOSED && // can't be connecting if the room is closed
        (localParticipantStatus !== streem.api.RoomParticipant.Status.STATUS_ACTIVE ||
            remoteParticipantStatus !== streem.api.RoomParticipant.Status.STATUS_ACTIVE) && // if either user isn't in
        invitationStatus !== streem.api.RoomInvitation.Status.STATUS_REJECTED && // we're disconnected
        invitationStatus !== streem.api.RoomInvitation.Status.STATUS_CANCELED && // we're disconnected
        invitationStatus !== streem.api.RoomInvitation.Status.STATUS_TIMED_OUT);
    if (retVal) {
        logger.debug(`Room CONNECTING Conditions Met: roomStatus=${roomStatus} localParticipantStatus=${localParticipantStatus} remoteParticipantStatus=${remoteParticipantStatus} invitationStatus=${invitationStatus}`);
    }
    return retVal;
};
const checkConnectedConditions = (config) => {
    const { roomStatus, localParticipantStatus, remoteParticipantStatus } = config;
    // the room is open and both users are in it, were connected
    // this means if say the remote user were to leave, we would not be connected,
    // which is what we want (even though the local user is)
    const retVal = (localParticipantStatus === streem.api.RoomParticipant.Status.STATUS_ACTIVE &&
        remoteParticipantStatus === streem.api.RoomParticipant.Status.STATUS_ACTIVE &&
        roomStatus === streem.api.Room.Status.STATUS_OPEN);
    if (retVal) {
        logger.debug(`Room CONNECTED Conditions Met: roomStatus=${roomStatus} localParticipantStatus=${localParticipantStatus} remoteParticipantStatus=${remoteParticipantStatus}`);
    }
    return retVal;
};
const checkDisconnectingConditions = (config) => {
    const { roomStatus, invitationStatus, localParticipantStatus } = config;
    const retVal = (roomStatus !== streem.api.Room.Status.STATUS_CLOSED && // closed would mean already disconnected
        ![
            undefined,
            streem.api.RoomParticipant.Status.STATUS_CONNECTED,
            streem.api.RoomParticipant.Status.STATUS_ACTIVE,
            streem.api.RoomParticipant.Status.STATUS_NEVER_ARRIVED
        ].includes(localParticipantStatus) &&
        invitationStatus !== streem.api.RoomInvitation.Status.STATUS_REJECTED && // never connected
        invitationStatus !== streem.api.RoomInvitation.Status.STATUS_CANCELED && // never connected
        invitationStatus !== streem.api.RoomInvitation.Status.STATUS_TIMED_OUT);
    if (retVal) {
        logger.debug(`Room DISCONNECTING Conditions Met: roomStatus=${roomStatus} localParticipantStatus=${localParticipantStatus} invitationStatus=${invitationStatus}`);
    }
    return retVal;
};
//# sourceMappingURL=create_room_status_observable.js.map